'use client';

import { useEffect, useState } from 'react';
import {
  CheckCircle2, Gavel, ShieldAlert, Undo2, Send, FileText, Download, AlertTriangle, Pencil, PenLine, Users,
  Search, Scale, type LucideIcon,
} from 'lucide-react';
import { avisoError, avisoExito } from '@/lib/avisoCentral';
import { MOSTRAR_NOTIFICACION_INDAGACION } from '@/lib/configModulo';
import type { ExpedienteDetalle as ExpDetalle } from '@/lib/expedientes';
import { usuarioActual } from '@/lib/instruccion';
import {
  TIPOS_DECISION, labelTipoDecision, labelEstadoDecision,
  ROLES_ESPECIALISTA, ROLES_DIRECTOR_DECISION,
  ROLES_CORRECTOR_ETAPA2, ROLES_CORRECTOR_TECNICO, ROLES_PROPONE_DECISION, ROLES_DECISION_UI,
  proponerDecision, devolverEspecialista, reenviarDecision,
  aprobarEspecialista, aprobarDecision, devolverDirector,
  listarRevisores, caminoDeterminado, origenCaminoLabel,
  obtenerDecision, avanzarPasoDecision, devolverPasoDecision,
  type TipoDecisionInicial, type DestinoDevolucion, type RevisoresDisponibles,
  type CaminoDeterminado, type DecisionInicial, type DecisionFlujoPaso,
} from '@/lib/decisionFlujo';
import FlujoActaTracker from '@/components/FlujoActaTracker';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import AutoInhibitorioDialog from '@/components/AutoInhibitorioDialog';
import IndagacionPreviaForm from '@/components/decisiones/IndagacionPreviaForm';
import InvestigacionForm from '@/components/decisiones/InvestigacionForm';
import DocumentoDecisionDialog from '@/components/decisiones/DocumentoDecisionDialog';
import CierreInhibitorio from '@/components/decisiones/CierreInhibitorio';
import {
  DOC_AUTO_PRORROGA, DOC_VERSION_LIBRE,
  type DocDecisionConfig,
} from '@/lib/plantillas/decisiones-docs';
import { docFlujoDe, subirDocFlujoAlGestor, etapaTrasPaso } from '@/lib/plantillas/documentos-flujo';
import { listarFirmantes } from '@/lib/preRadicacion';
import CampoPDF from '@/components/CampoPDF';
import { ContenidoEtapa, EncabezadoEtapa, SiNo } from '@/components/decisiones/decisionesUI';
import { descargarDocumento } from '@/lib/documentos';

// Documentos que el contratista realmente eligió (según la selección persistida en
// documento_datos), para que el revisor/Director vean SOLO esos, con el texto guardado.
// El Auto Inhibitorio ya tiene su propio bloque arriba. Si no hay selección (casos
// antiguos), se muestran todos los del tipo como respaldo.
function docsElegidos(exp: ExpDetalle): DocDecisionConfig[] {
  const tipo = exp.decision_inicial?.tipo;
  const sel = exp.decision_inicial?.documento_datos?.seleccion;
  if (tipo === 'INVESTIGACION') {
    if (!sel) return [DOC_AUTO_PRORROGA, DOC_VERSION_LIBRE];
    const docs: DocDecisionConfig[] = [];
    if (sel.prorroga1 || sel.prorroga2) docs.push(DOC_AUTO_PRORROGA);
    if (sel.versionLibre) docs.push(DOC_VERSION_LIBRE);
    return docs;
  }
  // Indagación previa: sus autos (indagación, apertura, terminación) van en sus propios
  // bloques con flujo de firmas.
  if (tipo === 'INDAGACION_PREVIA') return [];
  return [];
}

function DocumentosDecision({ exp }: { exp: ExpDetalle }) {
  const tipo = exp.decision_inicial?.tipo;
  // El Auto Inhibitorio se previsualiza en su propio bloque (arriba); aquí solo
  // gestionamos los documentos de Investigación / Indagación.
  const [abierto, setAbierto] = useState<DocDecisionConfig | null>(null);
  if (tipo !== 'INVESTIGACION' && tipo !== 'INDAGACION_PREVIA') return null;
  const docs = docsElegidos(exp);
  const guardados = exp.decision_inicial?.documento_datos?.docs;
  if (docs.length === 0) {
    if (tipo === 'INDAGACION_PREVIA') return null;
    return (
      <p className="text-sm text-muted-foreground">
        El contratista aún no generó documentos para esta decisión.
      </p>
    );
  }
  return (
    <div className="space-y-2 rounded-md border border-primary/30 bg-primary/5 p-3">
      <p className="flex items-center gap-2 text-sm font-medium">
        <FileText className="size-4 text-primary" /> Documentos de la decisión (previsualizar)
      </p>
      <div className="flex flex-wrap gap-2">
        {docs.map((d) => (
          <Button key={d.plantillaId} size="sm" variant="outline" onClick={() => setAbierto(d)}>
            <FileText className="size-3.5" /> {d.titulo}
          </Button>
        ))}
      </div>
      {abierto && (
        <DocumentoDecisionDialog
          exp={exp} cfg={abierto} soloLectura
          guardado={guardados?.[abierto.plantillaId]}
          onClose={() => setAbierto(null)}
        />
      )}
    </div>
  );
}

/**
 * Panel del flujo de decisión inicial (doble visto bueno) sobre la queja radicada.
 * Se muestra en la pestaña Instrucción mientras la decisión no esté en firme.
 * El contenido y las acciones dependen del estado del trámite y del rol en sesión.
 */
export default function PanelDecisionInicial({
  exp, onCambio, onEditarEtapa2,
}: { exp: ExpDetalle; onCambio: () => void; onEditarEtapa2?: (id: string) => void }) {
  const rol = usuarioActual()?.rol ?? '';
  const miId = usuarioActual()?.id ?? '';
  const decision = exp.decision_inicial;
  // Camino que la etapa anterior (comité / reparto) ya definió para este proceso.
  const camino = caminoDeterminado(exp.acta_reparto);
  const [ocupado, setOcupado] = useState(false);
  // Arranca con el tipo ya propuesto; si no hay, con el camino definido antes.
  const [tipo, setTipo] = useState<TipoDecisionInicial | ''>(
    (decision?.tipo as TipoDecisionInicial) ?? camino?.tipo ?? '',
  );
  // Notificación al personal (Sí/No) + constancia PDF cuando es "Sí".
  const [notificado, setNotificado] = useState<boolean | null>(decision?.notificado ?? null);
  const [notificacionKey, setNotificacionKey] = useState(decision?.notificacion_constancia_key ?? '');
  // Revisores destino: abogado especializado (obligatorio) y supervisor (opcional).
  const [revisorAbogadoId, setRevisorAbogadoId] = useState(decision?.revisor_abogado_id ?? '');
  const [revisorSupervisorId, setRevisorSupervisorId] = useState(decision?.revisor_supervisor_id ?? '');
  const [revisores, setRevisores] = useState<RevisoresDisponibles | null>(null);
  // Modal de devolución: 'ESP' = abogado/supervisor revisor, 'DIR' = director. null = cerrado.
  const [devolviendo, setDevolviendo] = useState<'ESP' | 'DIR' | null>(null);
  const [autoDoc, setAutoDoc] = useState(false);
  // Flujo de firmas del auto (estilo acta): se carga aparte para el tracker
  // y para saber si al usuario le toca firmar/aprobar ahora.
  const [flujoDec, setFlujoDec] = useState<DecisionInicial | null>(null);
  const [devolviendoFlujo, setDevolviendoFlujo] = useState(false);

  const puedeProponerRol = ROLES_PROPONE_DECISION.includes(rol);
  // Carga el flujo de firmas cuando el Auto inhibitorio lo está recorriendo.
  useEffect(() => {
    if (decision?.estado !== 'EN_FLUJO_FIRMA') { setFlujoDec(null); return; }
    if (!ROLES_DECISION_UI.includes(rol)) return;
    obtenerDecision(exp.id).then(setFlujoDec).catch(() => setFlujoDec(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decision?.estado, exp.id, rol]);
  // Carga la lista de abogados especializados y supervisores cuando toca elegir revisor.
  useEffect(() => {
    if (!puedeProponerRol) return;
    if (decision?.estado !== 'PENDIENTE_CONTRATISTA' && decision?.estado !== 'DEVUELTA_ETAPA2') return;
    listarRevisores().then(setRevisores).catch(() => setRevisores(null));
  }, [puedeProponerRol, decision?.estado]);

  if (!decision) return null;
  const estado = decision.estado;
  // Auto con flujo de firmas (inhibitorio o indagación previa): se puede generar apenas
  // se elige esa decisión (tipo en el formulario) o si ya quedó propuesta/aprobada.
  const docFlujo = docFlujoDe(tipo || decision.tipo);
  // Editores del documento: contratista / tecnólogo (+admin) — arman la decisión.
  // El resto (abogado especializado, Director, supervisión) lo ve en solo lectura
  // (previsualizar + PDF).
  const editorDoc = ROLES_PROPONE_DECISION.includes(rol);

  const puedeProponer = puedeProponerRol;
  // Cierre del Auto inhibitorio (comunicación al quejoso + estado del proceso):
  // lo diligencia quien lleva el caso, el Director o el admin.
  const esInhibitorio = (tipo || decision.tipo) === 'AUTO_INHIBITORIO';
  const editaCierre = puedeProponerRol || ROLES_DIRECTOR_DECISION.includes(rol);
  const enPropuesta = estado === 'PENDIENTE_CONTRATISTA' || estado === 'DEVUELTA_ETAPA2';
  const esDirector = ROLES_DIRECTOR_DECISION.includes(rol);
  const puedeCorregirTecnico = ROLES_CORRECTOR_TECNICO.includes(rol);
  // ¿Es el revisor asignado (abogado o supervisor) que puede dar visto bueno / devolver?
  // El admin recorre todo; el legado sin revisor cae al rol de abogado especializado.
  const sinRevisorAsignado = !decision.revisor_abogado_id && !decision.revisor_supervisor_id;
  const esRevisorAsignado =
    rol === 'ADMINISTRADOR' ||
    (!!miId && (miId === decision.revisor_abogado_id || miId === decision.revisor_supervisor_id)) ||
    (sinRevisorAsignado && ROLES_ESPECIALISTA.includes(rol));

  async function accion(fn: () => Promise<unknown>, msg: string) {
    setOcupado(true);
    try {
      await fn();
      avisoExito(msg);
      onCambio();
    } catch (e) {
      avisoError('No se pudo completar la acción', e instanceof Error ? e.message : undefined);
    } finally {
      setOcupado(false);
    }
  }

  async function enviar(conSupervisor: boolean) {
    if (!tipo) { avisoError('Seleccione una decisión.'); return; }
    // La notificación al personal solo aplica a Indagación previa Y si está
    // habilitada (Orden 11-B: por defecto RETIRADA, tras flag configurable).
    const esIndagacion = tipo === 'INDAGACION_PREVIA' && MOSTRAR_NOTIFICACION_INDAGACION;
    if (esIndagacion) {
      if (notificado === null) { avisoError('Indique si se pudo notificar al personal.'); return; }
      if (notificado && !notificacionKey) { avisoError('Adjunte la constancia de notificación (PDF).'); return; }
    }
    if (!revisorAbogadoId) { avisoError('Seleccione el abogado especializado.'); return; }
    if (conSupervisor && !revisorSupervisorId) { avisoError('Seleccione el supervisor.'); return; }
    await accion(
      () => proponerDecision(exp.id, {
        tipo,
        notificado: esIndagacion ? (notificado ?? undefined) : undefined,
        notificacionKey: esIndagacion && notificado ? notificacionKey : undefined,
        revisorAbogadoId,
        revisorSupervisorId: conSupervisor ? revisorSupervisorId : undefined,
      }),
      conSupervisor
        ? 'Decisión enviada al abogado especializado y al supervisor.'
        : 'Decisión enviada al abogado especializado.',
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <Gavel className="size-5 text-primary" /> Decisión inicial
          <Badge variant={estado === 'APROBADA' ? 'default' : 'secondary'}>
            {labelEstadoDecision(estado)}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Avisos de devolución */}
        {decision.motivo_devolucion && estado !== 'APROBADA' && (
          <Banner>
            <strong>Motivo de la devolución:</strong> {decision.motivo_devolucion}
          </Banner>
        )}
        {estado === 'DEVUELTA_ESPECIALISTA' && decision.comentarios_director && (
          <Banner>
            <strong>Comentarios del Director:</strong> {decision.comentarios_director}
          </Banner>
        )}

        {/* Documento del auto (inhibitorio / indagación previa): editable + previsualizable + descargable.
            En la fase de propuesta (contratista/tecnólogo eligiendo) esta caja se muestra
            DEBAJO de la decisión elegida (ver abajo); aquí arriba solo aparece en las fases
            de revisión/aprobación, para el abogado/Director/supervisión. */}
        {docFlujo && estado !== 'PENDIENTE_CONTRATISTA' && estado !== 'DEVUELTA_ETAPA2' && (
          <div className="rounded-md border border-primary/30 bg-primary/5 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm">
                <FileText className="mr-1 inline size-4 text-primary" />
                Documento <strong>{docFlujo.titulo}</strong> a partir de los datos del expediente.
              </span>
              <Button size="sm" variant="outline" onClick={() => setAutoDoc(true)}>
                <FileText className="size-3.5" /> {editorDoc ? 'Generar / previsualizar' : 'Previsualizar'}
              </Button>
            </div>
          </div>
        )}

        {/* ── Contratista / tecnólogo: eligen la decisión y proponen ── */}
        {(estado === 'PENDIENTE_CONTRATISTA' || estado === 'DEVUELTA_ETAPA2') && (
          puedeProponer ? (
            <div className="space-y-4">
              {onEditarEtapa2 && (
                <AccesoEditarEtapa2 disabled={ocupado} onClick={() => onEditarEtapa2(exp.id)} />
              )}
              {/* La etapa anterior (comité/reparto) ya definió el proceso: se muestra UNA
                  sola tarjeta con el proceso asignado. Solo en casos sin ruta (legado) se
                  ofrece elegir. El formulario del proceso aparece debajo. */}
              {camino ? (
                <TarjetaProcesoAsignado camino={camino} />
              ) : (
                <div>
                  <Label className="mb-2 block">Elija la decisión</Label>
                  <div className="grid gap-3 sm:grid-cols-3">
                    {TIPOS_DECISION.map((t) => (
                      <button
                        key={t.v}
                        type="button"
                        onClick={() => setTipo(t.v)}
                        className={cn(
                          'flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors hover:bg-accent',
                          tipo === t.v && 'border-primary ring-1 ring-primary bg-accent',
                        )}
                      >
                        <span className="text-sm font-medium">{t.label}</span>
                        <span className="text-xs font-medium text-primary">{t.norma}</span>
                        <span className="text-xs text-muted-foreground">{t.descripcion}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Diligenciamiento de la decisión asignada (debajo de la tarjeta, dentro de la etapa).
                  Auto inhibitorio → documento; Indagación / Investigación → su formulario. */}
              <ContenidoEtapa>
              {tipo === 'AUTO_INHIBITORIO' && (
                <Seccion icon={FileText} titulo="Documento y flujo de firmas">
                  <p className="text-sm text-muted-foreground">
                    Genere y edite el <strong>Auto inhibitorio (Art. 209)</strong> sobre la plantilla,
                    arme el <strong>flujo de firmas</strong> (agregue las personas y marque quiénes
                    firman) y envíelo: cada persona lo verá en su bandeja cuando sea su turno.
                  </p>
                  <Button size="sm" onClick={() => setAutoDoc(true)}>
                    <FileText className="size-3.5" /> {editorDoc ? 'Generar / editar / enviar' : 'Previsualizar'}
                  </Button>
                </Seccion>
              )}
              {tipo === 'AUTO_INHIBITORIO' && (
                <CierreInhibitorio exp={exp} editable={editaCierre} onCambio={onCambio} />
              )}
              {tipo === 'INDAGACION_PREVIA' && (
                <Seccion icon={FileText} titulo="Auto de indagación previa — documento y flujo de firmas">
                  <p className="text-sm text-muted-foreground">
                    Primer documento de la indagación: genere el <strong>Auto de indagación previa
                    (Art. 208)</strong> con los datos del expediente, complételo sobre la plantilla, arme
                    el <strong>flujo de firmas</strong> y envíelo. Lleva la firma de quien lo
                    elaboró, del <strong>abogado sustanciador</strong> y del <strong>director</strong>.
                  </p>
                  <Button size="sm" onClick={() => setAutoDoc(true)}>
                    <FileText className="size-3.5" /> {editorDoc ? 'Generar / editar / enviar' : 'Previsualizar'}
                  </Button>
                </Seccion>
              )}
              {tipo === 'INDAGACION_PREVIA' && (
                <Seccion icon={Search} titulo="Diligenciamiento — Indagación previa">
                  <IndagacionPreviaForm exp={exp} onCambio={onCambio} />
                </Seccion>
              )}
              {tipo === 'INVESTIGACION' && (
                <Seccion icon={Scale} titulo="Diligenciamiento — Investigación disciplinaria">
                  <InvestigacionForm exp={exp} onCambio={onCambio} />
                </Seccion>
              )}
              </ContenidoEtapa>

              {/* Notificación al personal: Indagación previa Y flag habilitado
                  (Orden 11-B: por defecto RETIRADO, configurable). */}
              {tipo === 'INDAGACION_PREVIA' && MOSTRAR_NOTIFICACION_INDAGACION && (
                <div className="space-y-2 rounded-lg border p-4">
                  <SiNo
                    label="¿Se pudo notificar al personal?"
                    value={notificado}
                    onChange={(v) => { setNotificado(v); if (!v) setNotificacionKey(''); }}
                  >
                    <CampoPDF
                      label="Constancia de notificación (PDF)"
                      carpeta="ANEXOS"
                      expedienteId={exp.id}
                      tipoDocumento="Constancia de notificación"
                      value={notificacionKey || undefined}
                      onChange={(path) => setNotificacionKey(path)}
                    />
                  </SiNo>
                  {notificado === false && (
                    <p className="text-xs text-muted-foreground">
                      Se guardará como <strong>no notificado</strong>.
                    </p>
                  )}
                </div>
              )}

              {/* Revisores destino: abogado especializado (obligatorio) + supervisor (opcional).
                  El Auto inhibitorio y la Indagación previa NO usan esta sección: su envío es
                  por flujo de firmas (dentro del documento). Aplica a Investigación. */}
              {!docFlujoDe(tipo) && (
              <Seccion icon={Users} titulo="Enviar a revisión">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Abogado especializado <span className="text-destructive">*</span></Label>
                    <Select value={revisorAbogadoId} onValueChange={setRevisorAbogadoId}>
                      <SelectTrigger className="w-full"><SelectValue placeholder="Seleccione…" /></SelectTrigger>
                      <SelectContent>
                        {(revisores?.abogados ?? []).map((a) => (
                          <SelectItem key={a.id} value={a.id}>{a.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Supervisor (opcional)</Label>
                    <Select value={revisorSupervisorId} onValueChange={setRevisorSupervisorId}>
                      <SelectTrigger className="w-full"><SelectValue placeholder="Seleccione…" /></SelectTrigger>
                      <SelectContent>
                        {(revisores?.supervisores ?? []).map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button disabled={ocupado || !tipo} onClick={() => enviar(false)}>
                    <Send className="size-3.5" /> Enviar al abogado especializado
                  </Button>
                  <Button variant="outline" disabled={ocupado || !tipo} onClick={() => enviar(true)}>
                    <Send className="size-3.5" /> Enviar a abogado especializado y supervisor
                  </Button>
                </div>
              </Seccion>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              El expediente está pendiente de que el contratista/tecnólogo elija la decisión.
            </p>
          )
        )}

        {/* ── Auto EN FLUJO DE FIRMAS: cada persona firma/aprueba en su turno ── */}
        {estado === 'EN_FLUJO_FIRMA' && (
          <div className="space-y-4">
            <Resumen tipo={decision.tipo} observaciones={decision.observaciones} decision={decision} />
            {(flujoDec?.pasos_flujo?.length ?? 0) > 0 && (
              <FlujoActaTracker
                pasos={flujoDec!.pasos_flujo as DecisionFlujoPaso[]}
                titulo="Flujo de firma — etapa actual"
              />
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setAutoDoc(true)}>
                <FileText className="size-3.5" /> Ver documento
              </Button>
            </div>
            {flujoDec?.flujo_mi_turno ? (
              <>
                <Separator />
                <p className="text-sm text-muted-foreground">
                  Es su turno en el flujo. Revise el documento y <strong>fírmelo/apruébelo</strong> para
                  pasarlo al siguiente, o devuélvalo con una observación.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    disabled={ocupado}
                    onClick={() => accion(
                      async () => {
                        const d = await avanzarPasoDecision(exp.id);
                        // Copia firmada al gestor documental (no frena el flujo si falla).
                        if (docFlujo) {
                          await listarFirmantes()
                            .then((f) => subirDocFlujoAlGestor(docFlujo, exp, d, f,
                              etapaTrasPaso(d, usuarioActual()?.nombre)))
                            .catch(() => avisoError('El documento no se pudo subir al gestor documental.'));
                        }
                        return d;
                      },
                      `Firmado/aprobado. El ${docFlujo?.nombre ?? 'documento'} avanzó en el flujo.`,
                    )}
                  >
                    <PenLine className="size-3.5" /> Firmar / aprobar
                  </Button>
                  <Button variant="outline" disabled={ocupado} onClick={() => setDevolviendoFlujo(true)}>
                    <Undo2 className="size-3.5" /> Devolver
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                El {docFlujo?.nombre ?? 'documento'} está recorriendo el flujo de firmas. Le llegará un aviso
                cuando sea su turno de revisar/firmar.
              </p>
            )}
          </div>
        )}

        {/* ── Revisor asignado (abogado especializado o supervisor): aprueba o devuelve ── */}
        {estado === 'PENDIENTE_ESPECIALISTA' && (
          <div className="space-y-4">
            <Resumen tipo={decision.tipo} observaciones={decision.observaciones} decision={decision} />
            <DocumentosDecision exp={exp} />
            {esRevisorAsignado ? (
              <>
                <Separator />
                <p className="text-sm text-muted-foreground">
                  Revise el documento (previsualícelo arriba). Si está conforme, dé el visto bueno
                  y fírmelo para pasarlo al Director; si no, devuélvalo a corregir.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    disabled={ocupado}
                    onClick={() => accion(
                      () => aprobarEspecialista(exp.id),
                      'Visto bueno dado y firmado. La decisión pasó al Director de Instrucción.',
                    )}
                  >
                    <PenLine className="size-3.5" /> Dar visto bueno y firmar
                  </Button>
                  <Button variant="outline" disabled={ocupado} onClick={() => setDevolviendo('ESP')}>
                    <Undo2 className="size-3.5" /> Devolver a corregir
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                El expediente está pendiente de la revisión del abogado especializado
                {decision.revisor_supervisor_id ? ' o del supervisor' : ''} asignado.
              </p>
            )}
          </div>
        )}

        {/* ── Director de Instrucción: aprueba (materializa) o devuelve ── */}
        {estado === 'PENDIENTE_DIRECTOR' && (
          <div className="space-y-4">
            <Resumen tipo={decision.tipo} observaciones={decision.observaciones} decision={decision} />
            <DocumentosDecision exp={exp} />
            {esDirector ? (
              <>
                <Separator />
                <div className="flex flex-wrap gap-2">
                  <Button
                    disabled={ocupado}
                    onClick={() => accion(
                      () => aprobarDecision(exp.id),
                      'Decisión aprobada y firmada; aplicada al expediente.',
                    )}
                  >
                    <PenLine className="size-3.5" /> Aprobar y firmar documento
                  </Button>
                  <Button variant="outline" disabled={ocupado} onClick={() => setDevolviendo('DIR')}>
                    <Undo2 className="size-3.5" /> Devolver a corregir
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                La propuesta está pendiente del visto bueno del Director de Instrucción.
              </p>
            )}
          </div>
        )}

        {/* ── Devuelta al técnico (Etapa 1) ── */}
        {/* Auto inhibitorio fuera de la propuesta (en firmas / aprobado): el cierre sigue aquí. */}
        {esInhibitorio && !enPropuesta && (
          <CierreInhibitorio exp={exp} editable={editaCierre} onCambio={onCambio} />
        )}

        {estado === 'DEVUELTA_TECNICO' && (
          <CorreccionReenvio
            texto="Corrija la pre-radicación (Etapa 1) y reenvíe el expediente al abogado especializado."
            puede={puedeCorregirTecnico}
            ocupado={ocupado}
            onReenviar={() => accion(() => reenviarDecision(exp.id), 'Expediente reenviado al abogado especializado.')}
          />
        )}

        {/* ── Decisión en firme ── */}
        {estado === 'APROBADA' && (
          <div className="space-y-3">
            {/* Los datos de la Etapa 2 se pueden corregir en cualquier momento del proceso. */}
            {onEditarEtapa2 && ROLES_CORRECTOR_ETAPA2.includes(rol) && (
              <AccesoEditarEtapa2 disabled={ocupado} onClick={() => onEditarEtapa2(exp.id)} />
            )}
            <Resumen tipo={decision.tipo} observaciones={decision.observaciones} decision={decision} />
            <DocumentosDecision exp={exp} />
            <div className="flex items-center gap-2 rounded-md bg-primary/5 p-3 text-sm text-primary">
              <CheckCircle2 className="size-4" /> La decisión quedó en firme y se aplicó al expediente.
            </div>
            {/* Indagación previa en curso: pruebas y decisión final (apertura / terminación). */}
            {decision.tipo === 'INDAGACION_PREVIA' && (
              <div className="space-y-4">
                <div className="overflow-hidden rounded-xl shadow-sm">
                  <EncabezadoEtapa n={1} titulo="Indagación previa" norma="Art. 208" estado="En curso"
                    descripcion="Pruebas y decisión final: apertura de la investigación o terminación." />
                </div>
                <ContenidoEtapa>
                  <Seccion icon={Search} titulo="Diligenciamiento — Indagación previa">
                    <IndagacionPreviaForm exp={exp} onCambio={onCambio} />
                  </Seccion>
                </ContenidoEtapa>
              </div>
            )}
            {/* Investigación abierta: todo su procedimiento (notificaciones, versión libre,
                pruebas con trazabilidad, recursos, acumulación…) sigue aquí. */}
            {decision.tipo === 'INVESTIGACION' && (
              <div className="space-y-4">
                <div className="overflow-hidden rounded-xl shadow-sm">
                  <EncabezadoEtapa n={1} titulo="Investigación disciplinaria" norma="Art. 211-213" estado="En curso"
                    descripcion="Procedimiento: notificaciones, versión libre, pruebas, recursos, defensa, cierre y evaluación." />
                </div>
                <ContenidoEtapa>
                  <Seccion icon={Scale} titulo="Diligenciamiento — Investigación disciplinaria">
                    <InvestigacionForm exp={exp} onCambio={onCambio}
                      fechaFija={(exp as { investigacion?: { fecha_auto_apertura?: string | null } | null }).investigacion?.fecha_auto_apertura?.slice(0, 10) ?? null}
                      avisoFecha="Automática: fecha del auto de apertura de la investigación." />
                  </Seccion>
                </ContenidoEtapa>
              </div>
            )}
          </div>
        )}
      </CardContent>

      {devolviendo && (
        <ModalDevolver
          radicado={exp.radicado}
          quien={devolviendo}
          onClose={() => setDevolviendo(null)}
          onListo={() => { setDevolviendo(null); onCambio(); }}
          onDevolver={(destino, motivo) =>
            devolviendo === 'DIR'
              ? devolverDirector(exp.id, { destino, motivo })
              : devolverEspecialista(exp.id, { destino, motivo })
          }
        />
      )}
      {autoDoc && docFlujo && (
        <AutoInhibitorioDialog
          exp={exp}
          doc={docFlujo}
          onClose={() => setAutoDoc(false)}
          soloLectura={!editorDoc}
          guardado={decision.documento_datos}
          onGuardado={onCambio}
          // El contratista arma y envía el flujo de firmas desde el documento.
          modoFlujo={editorDoc
            && (estado === 'PENDIENTE_CONTRATISTA' || estado === 'DEVUELTA_ETAPA2')}
          onEnviado={() => { setAutoDoc(false); onCambio(); }}
        />
      )}
      {devolviendoFlujo && (
        <ModalMotivoFlujo
          radicado={exp.radicado}
          onClose={() => setDevolviendoFlujo(false)}
          onListo={() => { setDevolviendoFlujo(false); onCambio(); }}
          onDevolver={(motivo) => devolverPasoDecision(exp.id, motivo)}
        />
      )}
    </Card>
  );
}

// ---- Modal: devolver en el flujo de firmas (un paso atrás) con observación ----
function ModalMotivoFlujo({
  radicado, onClose, onListo, onDevolver,
}: {
  radicado: string;
  onClose: () => void;
  onListo: () => void;
  onDevolver: (motivo: string) => Promise<unknown>;
}) {
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);
  async function enviar() {
    if (motivo.trim().length < 3) { avisoError('Indique el motivo de la devolución.'); return; }
    setGuardando(true);
    try {
      await onDevolver(motivo.trim());
      avisoExito('Documento devuelto en el flujo.');
      onListo();
    } catch (e) {
      avisoError('No se pudo devolver', e instanceof Error ? e.message : undefined);
    } finally {
      setGuardando(false);
    }
  }
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Undo2 className="size-5 text-primary" /> Devolver en el flujo
          </DialogTitle>
          <DialogDescription>Radicado: <strong>{radicado}</strong></DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="motivo-flujo">Motivo</Label>
          <Textarea id="motivo-flujo" rows={3} value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Qué debe ajustarse antes de continuar." />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={guardando}>Cancelar</Button>
          <Button onClick={enviar} disabled={guardando}>
            {guardando ? 'Devolviendo…' : 'Devolver'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Sección con cabecera (icono + título) y cuerpo, para un look cohesivo en todo el panel.
function Seccion({
  icon: Icon, titulo, children,
}: { icon: LucideIcon; titulo: string; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-xl border border-primary/30 bg-card shadow-sm">
      {/* Cabecera en verde institucional (como «Proceso asignado») para distinguir cada sección. */}
      <div className="flex items-center gap-2 bg-gradient-to-r from-primary to-green-700 px-4 py-2.5 text-primary-foreground">
        <Icon className="size-4" />
        <span className="text-xs font-semibold uppercase tracking-wide">
          {titulo}
        </span>
      </div>
      <div className="space-y-3 p-5">{children}</div>
    </div>
  );
}

// Tarjeta única con el proceso que la etapa anterior (comité/reparto) definió.
// Reemplaza la elección entre los 3 botones cuando la ruta ya viene decidida.
function TarjetaProcesoAsignado({ camino }: { camino: CaminoDeterminado }) {
  const meta = TIPOS_DECISION.find((t) => t.v === camino.tipo);
  // Encabezado de ETAPA (no de sección): es la primera etapa de la instrucción.
  return (
    <div className="overflow-hidden rounded-xl shadow-sm">
      <EncabezadoEtapa n={1} titulo={meta?.label ?? 'Proceso asignado'} norma={meta?.norma}
        descripcion={meta?.descripcion} estado="En curso"
        extra={<span className="inline-flex items-center gap-1.5"><CheckCircle2 className="size-3.5" />
          Proceso asignado · ruta definida por {origenCaminoLabel(camino.origen)}</span>} />
    </div>
  );
}

// Acceso destacado para corregir los datos de la Etapa 2: tarjeta con borde verde
// punteado, ícono y botón sólido, para que no pase desapercibido.
function AccesoEditarEtapa2({ disabled, onClick }: { disabled?: boolean; onClick: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-xl border-2 border-dashed border-primary/40 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-4 shadow-sm">
      <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-md shadow-primary/25">
        <Pencil className="size-5" />
      </div>
      <div className="min-w-48 flex-1">
        <p className="font-semibold text-primary">Datos de la Etapa 2</p>
        <p className="text-sm text-muted-foreground">
          Revise o corrija la información registrada antes de continuar con la decisión.
        </p>
      </div>
      <Button
        size="lg"
        disabled={disabled}
        onClick={onClick}
        className="shadow-md shadow-primary/25 transition-transform hover:-translate-y-0.5"
      >
        <Pencil className="size-4" /> Editar datos de la Etapa 2
      </Button>
    </div>
  );
}

function Banner({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
      <AlertTriangle className="mt-0.5 size-4 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

function Resumen({
  tipo, observaciones, decision,
}: {
  tipo?: string | null;
  observaciones?: string | null;
  decision?: ExpDetalle['decision_inicial'];
}) {
  const notificado = decision?.notificado;
  const constancia = decision?.notificacion_constancia_key;
  return (
    <div className="space-y-1 rounded-md border p-3 text-sm">
      <p className="flex items-center gap-2">
        <FileText className="size-4 text-muted-foreground" />
        <span className="font-medium">Decisión propuesta:</span> {labelTipoDecision(tipo)}
      </p>
      {observaciones && <p className="text-muted-foreground">{observaciones}</p>}
      {notificado != null && (
        <p className="flex flex-wrap items-center gap-2 text-muted-foreground">
          <span className="font-medium">Notificación al personal:</span>
          {notificado ? 'Sí se notificó' : 'No se notificó'}
          {notificado && constancia && (
            <button
              type="button"
              onClick={() => descargarDocumento(constancia)}
              className="font-medium text-primary underline underline-offset-2"
            >
              ver constancia
            </button>
          )}
        </p>
      )}
    </div>
  );
}

function CorreccionReenvio({
  texto, puede, ocupado, onReenviar, onEditar,
}: { texto: string; puede: boolean; ocupado: boolean; onReenviar: () => void; onEditar?: () => void }) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{texto}</p>
      {puede ? (
        <div className="space-y-3">
          {onEditar && <AccesoEditarEtapa2 disabled={ocupado} onClick={onEditar} />}
          <Button disabled={ocupado} onClick={onReenviar}>
            <Send className="size-3.5" /> Reenviar al especialista
          </Button>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">El expediente está a la espera de corrección.</p>
      )}
    </div>
  );
}

// ---- Modal: devolver a corregir (abogado especializado o Director) ---------
// El destino elige a quién regresa: contratista/tecnólogo (Etapa 2 + decisión) o técnico (Etapa 1).
function ModalDevolver({
  radicado, quien, onClose, onListo, onDevolver,
}: {
  radicado: string;
  quien: 'ESP' | 'DIR';
  onClose: () => void;
  onListo: () => void;
  onDevolver: (destino: DestinoDevolucion, motivo: string) => Promise<unknown>;
}) {
  const [destino, setDestino] = useState<DestinoDevolucion>('ETAPA2');
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);

  async function enviar() {
    if (motivo.trim().length < 3) { avisoError('Indique el motivo de la devolución.'); return; }
    setGuardando(true);
    try {
      await onDevolver(destino, motivo.trim());
      avisoExito('Expediente devuelto a corrección.');
      onListo();
    } catch (e) {
      avisoError('No se pudo devolver el expediente', e instanceof Error ? e.message : undefined);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {quien === 'DIR' ? <ShieldAlert className="size-5 text-primary" /> : <Undo2 className="size-5 text-primary" />}
            Devolver a corregir
          </DialogTitle>
          <DialogDescription>Radicado: <strong>{radicado}</strong></DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>¿A quién se devuelve?</Label>
            <Select value={destino} onValueChange={(v) => setDestino(v as DestinoDevolucion)}>
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ETAPA2">Contratista / tecnólogo de apoyo (Etapa 2 + decisión)</SelectItem>
                <SelectItem value="TECNICO">Técnico administrativo (pre-radicación)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="motivo-dev">Motivo</Label>
            <Textarea id="motivo-dev" rows={3} value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Qué debe corregirse o completarse." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={guardando}>Cancelar</Button>
          <Button onClick={enviar} disabled={guardando}>
            {guardando ? 'Devolviendo…' : 'Devolver'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
